// The sprite atlas, on WebGPU (the runtime design §4).
//
// It is the twin of the `AtlasTexture` of `gl/texture.ts`: one RGBA8 texture
// built from a 2D canvas, uploaded PREMULTIPLIED so the one blend state fits
// it, with four mip levels.
//
// `copyExternalImageToTexture` takes the canvas straight, and
// `premultipliedAlpha: true` on the destination is the
// `UNPACK_PREMULTIPLY_ALPHA_WEBGL` of the WebGL2 path. The mip chain is
// drawn rather than generated (`mips.ts`), because WebGPU has no
// `generateMipmap`.

import { ATLAS_MAX_LEVEL } from '../gl/texture';
import { ATLAS_FORMAT, MipBlitter } from './mips';
import { TEXTURE_BINDING, TEXTURE_COPY_DST, TEXTURE_RENDER_ATTACHMENT } from './flags';
import { bindUnit } from './bindgroups';
import type { AtlasTexture as AtlasTextureApi } from '../gfx/types';
import type { Gpu, GpuResource } from './context';

/** How many mip levels the atlas carries. Level 3 is the floor (R548). */
const ATLAS_LEVELS = ATLAS_MAX_LEVEL + 1;

/** How many binds a stale sheet waits between two uploads (R3195). */
const HEAL_GAP_BINDS = 120;

export class GpuAtlasTexture implements GpuResource, AtlasTextureApi {
  texture: GPUTexture | null = null;
  /** The id the group-1 cache is keyed by. A new texture takes a new one. */
  id = 0;

  private view: GPUTextureView | null = null;
  private source: HTMLCanvasElement;
  private readonly mips = new MipBlitter();
  /**
   * True after a copy failed, so the texture misses part of the sheet
   * (R3195). Firefox throws `NS_ERROR_OUT_OF_MEMORY` from
   * `copyExternalImageToTexture` when it gets no shared memory for the copy.
   * The next `patch`, and a `bind` after `HEAL_GAP_BINDS` binds, upload the
   * whole source again until a copy succeeds.
   */
  private stale = false;
  private healWait = 0;
  private warned = false;

  constructor(private readonly gpu: Gpu, source: HTMLCanvasElement) {
    this.source = source;
    gpu.register(this);
  }

  /** Points the texture at another canvas, for instance after a grow. */
  setSource(source: HTMLCanvasElement): void {
    this.source = source;
    if (!this.gpu.lost) this.create(this.gpu.device);
  }

  create(device: GPUDevice): void {
    // A grow mid-pack runs inside an open frame whose draws may name the old
    // texture, so it is retired and not destroyed (R1340).
    if (this.texture) this.gpu.retire(this.texture);
    this.texture = null;
    this.destroy();
    this.mips.reset();
    const texture = device.createTexture({
      label: 'fast: atlas',
      size: [Math.max(1, this.source.width), Math.max(1, this.source.height)],
      format: ATLAS_FORMAT,
      mipLevelCount: ATLAS_LEVELS,
      // RENDER_ATTACHMENT, because the mip levels are DRAWN into.
      usage: TEXTURE_BINDING | TEXTURE_COPY_DST | TEXTURE_RENDER_ATTACHMENT,
    });
    this.stale = false;
    this.copy(this.source, texture, 0, 0);
    this.texture = texture;
    this.view = texture.createView();
    this.id = this.gpu.textureId();
    this.mips.build(device, texture, ATLAS_LEVELS);
  }

  /**
   * Uploads one rectangle again. The badge digits use it: the face lands
   * after the first bake, and the cell is redrawn in place.
   *
   * The caller draws the rectangle into the source canvas first, so a stale
   * texture takes the whole source in place of the rectangle (R3195).
   */
  patch(canvas: HTMLCanvasElement, x: number, y: number): void {
    const texture = this.texture;
    if (!texture || this.gpu.lost) return;
    if (this.stale) { this.heal(texture); return; }
    if (!this.copy(canvas, texture, x, y)) return;
    // The whole chain again: a patched cell whose lower levels hold the old
    // ink bleeds the old ink back at distance.
    this.mips.build(this.gpu.device, texture, ATLAS_LEVELS);
  }

  /** Uploads the whole source again and builds the chain, on a stale texture. */
  private heal(texture: GPUTexture): void {
    if (!this.copy(this.source, texture, 0, 0)) return;
    this.stale = false;
    this.mips.build(this.gpu.device, texture, ATLAS_LEVELS);
  }

  /**
   * Copies a canvas into level 0 at `(x, y)`, premultiplied. A copy that
   * throws marks the texture stale and answers false, so the frame goes on
   * with the old cells (R3195).
   */
  private copy(source: HTMLCanvasElement, texture: GPUTexture, x: number, y: number): boolean {
    try {
      this.gpu.device.queue.copyExternalImageToTexture(
        { source },
        { texture, origin: [x, y], premultipliedAlpha: true, colorSpace: 'srgb' },
        [Math.max(1, source.width), Math.max(1, source.height)],
      );
      return true;
    } catch (err) {
      this.stale = true;
      this.healWait = HEAL_GAP_BINDS;
      if (!this.warned) {
        this.warned = true;
        console.warn('fast: the atlas upload failed; it tries again', err);
      }
      return false;
    }
  }

  destroy(): void {
    if (this.texture) this.texture.destroy();
    this.texture = null;
    this.view = null;
    this.id = 0;
  }

  bind(unit: number): void {
    if (!this.view || this.gpu.lost) return;
    if (this.stale && this.texture && --this.healWait <= 0) this.heal(this.texture);
    bindUnit(this.gpu.units, unit, this.view, this.id);
  }

  dispose(): void { this.gpu.unregister(this); }
}
